import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  viewChildren
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import type { LogoModel } from '@amfam/shared/models/lib/brand/logo.model';
import { RouterLinkWithHrefDirective } from '@amfam/shared/ui/ds-router-link/lib/router-link.directive';
import { BrandLinkDataModel } from '@amfam/shared/utility/brand/lib/models/brand.model';
import { ContactDetails } from '@amfam/shared/utility/contact-us/lib/models/contact-us.model';

import { LinkClickEvent, NavLinkTreeObj, SublinkClickEvent } from '../../models/links-model';
import { NotificationObj } from '../../models/notification-model';
import { HeaderWrapperService } from '../../services/header-wrapper.service';
import { ContactDetailsComponent } from './ds-contact-details/ds-contact-details.component';
import { DsHeaderNavigationItemComponent } from './ds-header-navigation-item/ds-header-navigation-item.component';
import { DsHeaderNotificationComponent } from './ds-header-notification/ds-header-notification.component';

@Component({
  selector: 'ds-header-navigation',
  templateUrl: './ds-header-navigation.component.html',
  styleUrls: ['./ds-header-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DsHeaderNavigationItemComponent,
    DsHeaderNotificationComponent,
    ContactDetailsComponent,
    RouterLinkWithHrefDirective
  ]
})
export class DsHeaderNavigationComponent {
  private headerWrapperService = inject(HeaderWrapperService);

  // An event stream for the nav items, not template state; see DsHeaderComponent.
  readonly loaded$ = input.required<Observable<boolean>>();
  readonly headerLogo = input<LogoModel | null>();
  readonly brandInfo = input<BrandLinkDataModel | null>();
  readonly loggedIn = input<boolean | null>();
  readonly homeUrl = input.required<string>();
  readonly notifications = input<NotificationObj[] | null>();
  readonly linkArray = input<NavLinkTreeObj[] | null>();
  readonly contactDetails = input<ContactDetails | null>();

  readonly linkClickEvent = output<LinkClickEvent>();
  readonly sublinkClickEvent = output<SublinkClickEvent>();

  readonly navItems = viewChildren<DsHeaderNavigationItemComponent>('navItem');

  readonly headerNavRect = toSignal(this.headerWrapperService.headerNavRect$);

  hideAllSubMenus(navItem: DsHeaderNavigationItemComponent) {
    this.navItems().forEach(navItemInstance => {
      if (navItemInstance !== navItem) {
        if (navItemInstance.showSub() !== 'hide') {
          navItemInstance.showSub.set('hide');
        }
        if (navItemInstance.submenuExpanded() !== 'false') {
          navItemInstance.submenuExpanded.set('false');
        }
      }
    });
  }

  linkClicked(event: LinkClickEvent) {
    if (event) {
      this.linkClickEvent.emit(event);
    }
  }

  // Tell Angular to track these items by ID so that they are not recreated in the DOM
  trackByFn(index: number) {
    return index;
  }

  sublinkClicked(event: SublinkClickEvent) {
    if (event) {
      this.sublinkClickEvent.emit(event);
    }
  }
}
